import React, { useEffect, useMemo, useState } from "react";
import { api, STATUS_OPTIONS, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Edit3, Search, Filter, Link2, FileText } from "lucide-react";
import ServicoFormDialog from "../components/ServicoFormDialog";
import WorkOrderDialog from "../components/WorkOrderDialog";
import { toast } from "sonner";

export default function ServicosPage() {
    const [servicos, setServicos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [motos, setMotos] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("__all__");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [woOpen, setWoOpen] = useState(false);
    const [woServico, setWoServico] = useState(null);

    async function load() {
        const [sv, cl, mt] = await Promise.all([
            api.get("/servicos"),
            api.get("/clientes"),
            api.get("/motos"),
        ]);
        setServicos(sv.data);
        setClientes(cl.data);
        setMotos(mt.data);
    }
    useEffect(() => {
        load();
    }, []);

    const clienteById = useMemo(() => Object.fromEntries(clientes.map((c) => [c.id, c])), [clientes]);
    const motoById = useMemo(() => Object.fromEntries(motos.map((m) => [m.id, m])), [motos]);

    const filtered = servicos
        .filter((s) => statusFilter === "__all__" || s.status === statusFilter)
        .filter((s) => {
            if (!q) return true;
            const m = motoById[s.moto_id];
            const c = clienteById[s.cliente_id];
            const hay = [
                s.tipo,
                s.descricao,
                s.data,
                m?.marca,
                m?.modelo,
                m?.num_chassis,
                c?.nome,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(q.toLowerCase());
        })
        .sort((a, b) => (b.data + b.hora).localeCompare(a.data + a.hora));

    async function quickStatus(s, status) {
        try {
            await api.put(`/servicos/${s.id}`, { status });
            toast.success("Estado atualizado.");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        }
    }

    async function copyTrackingLink(s) {
        const url = `${window.location.origin}/track/${s.tracking_token}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link de acompanhamento copiado!");
        } catch {
            // Fallback: show prompt
            window.prompt("Copie o link de acompanhamento:", url);
        }
    }

    return (
        <>
            <PageHeader
                eyebrow="Histórico"
                title="Serviços"
                description="Histórico completo de marcações e trabalhos efetuados."
                testid="servicos-header"
                actions={
                    <Button
                        onClick={() => {
                            setEditing(null);
                            setOpen(true);
                        }}
                        className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        data-testid="new-servico-page-btn"
                        disabled={motos.length === 0}
                    >
                        <Plus className="mr-2 h-4 w-4" /> Nova marcação
                    </Button>
                }
            />

            {motos.length === 0 ? (
                <div className="mb-4 rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    Registe pelo menos uma moto antes de criar serviços.
                </div>
            ) : null}

            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="flex flex-1 min-w-[240px] max-w-md items-center gap-2 border border-zinc-200 bg-white px-3">
                    <Search className="h-4 w-4 text-zinc-400" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Procurar por tipo, cliente, moto..."
                        className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
                        data-testid="servicos-search-input"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-zinc-400" />
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-10 w-44 rounded-sm" data-testid="servicos-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Todos os estados</SelectItem>
                            {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="border border-zinc-200 bg-white" data-testid="servicos-table">
                <div className="grid grid-cols-12 gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                    <div className="col-span-2">Data / Hora</div>
                    <div className="col-span-2">Cliente</div>
                    <div className="col-span-2">Moto</div>
                    <div className="col-span-2">Tipo</div>
                    <div className="col-span-2">Estado</div>
                    <div className="col-span-2 text-right">Ações</div>
                </div>
                {filtered.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-zinc-500">
                        Sem serviços registados.
                    </div>
                ) : (
                    filtered.map((s) => {
                        const m = motoById[s.moto_id];
                        const c = clienteById[s.cliente_id];
                        return (
                            <div
                                key={s.id}
                                data-testid={`servico-row-${s.id}`}
                                className="grid grid-cols-12 items-center gap-2 border-b border-zinc-100 px-4 py-3 text-sm hover:bg-zinc-50"
                            >
                                <div className="col-span-2">
                                    <div className="font-mono text-xs text-zinc-900">{s.data}</div>
                                    <div className="font-mono text-[11px] text-zinc-500">{s.hora}</div>
                                </div>
                                <div className="col-span-2 text-zinc-800">{c?.nome || "—"}</div>
                                <div className="col-span-2 text-zinc-800">
                                    {m ? `${m.marca} ${m.modelo}` : "—"}
                                </div>
                                <div className="col-span-2">
                                    <div className="font-medium text-zinc-900">{s.tipo}</div>
                                    {s.numero ? (
                                        <div className="mt-0.5 inline-block rounded-sm bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-zinc-700">
                                            Nº {s.numero}
                                        </div>
                                    ) : null}
                                    {s.descricao ? (
                                        <div className="mt-0.5 truncate text-xs text-zinc-500">{s.descricao}</div>
                                    ) : null}
                                </div>
                                <div className="col-span-2">
                                    <Select
                                        value={s.status}
                                        onValueChange={(v) => quickStatus(s, v)}
                                    >
                                        <SelectTrigger
                                            className="h-8 rounded-sm"
                                            data-testid={`servico-status-select-${s.id}`}
                                        >
                                            <StatusBadge status={s.status} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-2 flex justify-end gap-1">
                                    {["em_curso", "concluido", "entregue"].includes(s.status) ? (
                                        <Button
                                            size="icon"
                                            variant="outline"
                                            className="h-7 w-7 rounded-sm text-zinc-700"
                                            onClick={() => {
                                                setWoServico(s);
                                                setWoOpen(true);
                                            }}
                                            title="Folha de Obra"
                                            data-testid={`servico-workorder-${s.id}`}
                                        >
                                            <FileText className="h-3.5 w-3.5" />
                                        </Button>
                                    ) : null}
                                    <Button
                                        size="icon"
                                        variant="outline"
                                        className="h-7 w-7 rounded-sm text-zinc-700"
                                        onClick={() => copyTrackingLink(s)}
                                        title="Copiar link de acompanhamento"
                                        data-testid={`servico-track-${s.id}`}
                                    >
                                        <Link2 className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="outline"
                                        className="h-7 w-7 rounded-sm"
                                        onClick={() => {
                                            setEditing(s);
                                            setOpen(true);
                                        }}
                                        data-testid={`servico-edit-${s.id}`}
                                    >
                                        <Edit3 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <ServicoFormDialog
                open={open}
                onClose={() => setOpen(false)}
                onSaved={() => {
                    setOpen(false);
                    load();
                }}
                clientes={clientes}
                motos={motos}
                initial={editing}
            />

            <WorkOrderDialog
                open={woOpen}
                onClose={() => setWoOpen(false)}
                servico={woServico}
                motoLabel={
                    woServico && motoById[woServico.moto_id]
                        ? `${motoById[woServico.moto_id].marca} ${motoById[woServico.moto_id].modelo}`
                        : ""
                }
                clienteNome={woServico ? clienteById[woServico.cliente_id]?.nome : ""}
                onSaved={() => {
                    setWoOpen(false);
                    load();
                }}
            />
        </>
    );
}
